import { Controller } from "@hotwired/stimulus"
import intlTelInput from "intl-tel-input"
import { forbiddenNanp911E164 } from "../utils/phone_number_rules"

export default class extends Controller {
  static targets = ["name", "email", "phoneIti", "phoneHidden", "phoneError", "subject", "message", "submit"]
  static values = {
    excludedCountries: { type: Array, default: [] },
  }

  connect() {
    this.captchaReady = false
    this.iti = null
    this.boundIntlSync = null
    this.boundSync = () => this.syncSubmitDisabled()
    this.nameTarget.addEventListener("input", this.boundSync)
    this.nameTarget.addEventListener("blur", this.boundSync)
    this.emailTarget.addEventListener("input", this.boundSync)
    this.emailTarget.addEventListener("blur", this.boundSync)
    this.subjectTarget.addEventListener("input", this.boundSync)
    this.subjectTarget.addEventListener("blur", this.boundSync)
    this.messageTarget.addEventListener("input", this.boundSync)
    this.messageTarget.addEventListener("blur", this.boundSync)
    this.initIntlPhone()
    this.syncSubmitDisabled()
  }

  disconnect() {
    this.nameTarget.removeEventListener("input", this.boundSync)
    this.nameTarget.removeEventListener("blur", this.boundSync)
    this.emailTarget.removeEventListener("input", this.boundSync)
    this.emailTarget.removeEventListener("blur", this.boundSync)
    this.subjectTarget.removeEventListener("input", this.boundSync)
    this.subjectTarget.removeEventListener("blur", this.boundSync)
    this.messageTarget.removeEventListener("input", this.boundSync)
    this.messageTarget.removeEventListener("blur", this.boundSync)
    if (this.iti && this.boundIntlSync) {
      this.phoneItiTarget.removeEventListener("countrychange", this.boundIntlSync)
      this.phoneItiTarget.removeEventListener("input", this.boundIntlSync)
      this.phoneItiTarget.removeEventListener("blur", this.boundIntlSync)
      this.iti.destroy()
      this.iti = null
    }
  }

  captchaVerified(event) {
    this.captchaReady = Boolean(event.detail?.token)
    this.syncSubmitDisabled()
  }

  captchaCleared() {
    this.captchaReady = false
    this.syncSubmitDisabled()
  }

  initIntlPhone() {
    this.boundIntlSync = () => {
      this.syncIntlPhoneToHidden()
      this.syncSubmitDisabled()
    }

    this.iti = intlTelInput(this.phoneItiTarget, {
      initialCountry: "us",
      countryOrder: ["us"],
      excludeCountries: this.excludedCountriesValue,
      loadUtils: () => import("intl-tel-input/utils"),
      customPlaceholder: (nationalExample, countryData) => {
        const prefix = countryData?.dialCodePlus
        if (!prefix) return nationalExample || ""
        if (!nationalExample) return prefix
        return `${prefix} ${nationalExample}`
      },
    })

    this.phoneItiTarget.addEventListener("countrychange", this.boundIntlSync)
    this.phoneItiTarget.addEventListener("input", this.boundIntlSync)
    this.phoneItiTarget.addEventListener("blur", this.boundIntlSync)

    this.iti.promise.then(() => {
      this.restoreIntlFromHidden()
      this.syncSubmitDisabled()
    })
  }

  restoreIntlFromHidden() {
    const v = this.phoneHiddenTarget.value.trim()
    if (!v || !this.iti) return
    this.iti.setNumber(v)
  }

  syncIntlPhoneToHidden() {
    if (!this.iti) return
    const trimmed = this.phoneItiTarget.value.trim()
    if (trimmed === "") {
      this.phoneHiddenTarget.value = ""
      return
    }
    if (!intlTelInput.utils) return
    const e164 = this.iti.getNumber()
    this.phoneHiddenTarget.value = e164 || ""
  }

  // After syncIntlPhoneToHidden: validity + user-facing error (empty error while libphonenumber loads).
  phoneFieldState() {
    const trimmed = this.phoneItiTarget.value.trim()
    if (trimmed === "") return { valid: true, error: "" }
    if (!this.iti || !intlTelInput.utils) return { valid: false, error: "" }

    this.syncIntlPhoneToHidden()
    const e164 = this.phoneHiddenTarget.value.trim()
    if (forbiddenNanp911E164(e164)) {
      return {
        valid: false,
        error: "911 cannot be used in the area code or as the first three digits of the local number.",
      }
    }

    if (this.iti.isValidNumber() === true) return { valid: true, error: "" }
    return { valid: false, error: "Enter a valid phone number for the selected country." }
  }

  applyPhoneErrorFeedback(message) {
    const show = message !== ""

    this.phoneErrorTarget.textContent = message
    this.phoneErrorTarget.classList.toggle("hidden", !show)
    if (show) {
      this.phoneItiTarget.setAttribute("aria-invalid", "true")
      this.phoneItiTarget.setAttribute("aria-errormessage", "contact-inquiry-phone-error")
    } else {
      this.phoneItiTarget.removeAttribute("aria-invalid")
      this.phoneItiTarget.removeAttribute("aria-errormessage")
    }
  }

  requiredFieldsValid(phoneValid) {
    const nameOk = this.nameTarget.value.trim().length > 0
    const subjectOk = this.subjectTarget.value.trim().length > 0
    const messageOk = this.messageTarget.value.trim().length > 0
    const emailEl = this.emailTarget
    return nameOk && subjectOk && messageOk && emailEl.checkValidity() && phoneValid
  }

  syncSubmitDisabled() {
    this.syncIntlPhoneToHidden()
    const phone = this.phoneFieldState()
    this.applyPhoneErrorFeedback(phone.error)
    const enable = this.requiredFieldsValid(phone.valid) && this.captchaReady
    this.submitTarget.disabled = !enable
  }
}
